// One open line to the realtime model: audio in, audio out, its own voice-activity detection.
// The Lab's archive is attached server-side as a tool, so grounding survives — when the model
// calls it, we run the retrieval here and hand the passages straight back over the data channel.
import { API } from "@/lib/api";
import { attachPlayback, attachRemote, attachStream, ctxState, detachStream, gainValue, playbackCaptured, releaseOutput, resume, setSource, setTrim, setVolume, speakerLive } from "@/lib/audioLevel";

const json = (o) => JSON.stringify(o);

// Where one spoken sentence ends. Griot's words go to a human voice a sentence at a time, so the
// first one is already in the air while the rest is still being written.
const SENT = /[.!?…](?=\s|$)/g;
const cutPoint = (buf) => {
  SENT.lastIndex = 0;
  let m;
  while ((m = SENT.exec(buf))) if (m.index >= 1) return m.index + m[0].length;
  // a long sentence that never lands: break it at a comma rather than hold the voice back
  if (buf.length > 170) {
    const c = buf.lastIndexOf(",", 170);
    if (c > 40) return c + 1;
    const s = buf.lastIndexOf(" ", 170);
    if (s > 40) return s + 1;
  }
  return 0;
};

// The FIRST fragment of a reply is cut short and sent to be spoken straight away — waiting for a
// whole sentence costs most of a second of silence, and a short fragment also synthesises faster.
// Everything after it is cut on sentences, by which time the voice is already talking.
const HEAD_MIN = 24;
const HEAD_MAX = 70;
const headCut = (buf) => {
  const whole = cutPoint(buf);
  if (whole > 0 && whole <= HEAD_MAX) return whole;
  if (buf.length < HEAD_MIN) return 0;
  const slice = buf.slice(0, HEAD_MAX);
  for (const ch of [",", ";", ":", "—", "–"]) {
    const i = slice.lastIndexOf(ch);
    if (i >= HEAD_MIN) return i + 1;
  }
  if (buf.length < HEAD_MAX) return whole > 0 ? whole : 0;
  const sp = slice.lastIndexOf(" ");
  return sp >= HEAD_MIN ? sp + 1 : HEAD_MAX;
};

export class RealtimeVoice {
  constructor(opts) {
    this.o = opts;
    this.pc = null;
    this.dc = null;
    this.el = null;
    this.stream = null;
    this.startedAt = 0;
    this.model = "";
    this.greeting = "";
    this.remote = null;
    this.speaker = opts.output !== "private";
    this.vol = 1;
    this.said = "";
    this.heard = "";
    this.sources = [];
    this.calls = {};
    this.closed = false;
    // the real-voice path: the model writes, ElevenLabs speaks, we play the sentences in order
    this.engine = "openai";
    this.voiceName = "";
    this.queue = [];
    this.buf = "";
    this.gen = 0;
    this.pumping = false;
    this.speakingNow = false;
    this.head = true;            // the next fragment is the first of a reply: cut it short
    this.t = { ask: 0 };         // where the time goes on a turn, for diagnosis
  }

  async start() {
    const stream = await micStream();
    this.stream = stream;
    attachStream(stream);
    resume();

    const pc = new RTCPeerConnection();
    this.pc = pc;

    const el = voiceElement();
    this.el = el;

    pc.ontrack = (e) => {
      if (this.engine === "elevenlabs") return;   // nothing comes down this track; a human voice speaks
      this.remote = e.streams[0];
      this._route();   // sets srcObject and starts playback
      // if the browser never started the audio context, the element has to carry the sound
      setTimeout(() => {
        if (this.vol === 0) return;   // they have silenced him on purpose; leave it alone
        if (el.muted && !speakerLive()) { el.muted = false; el.volume = this.vol ?? 1; }
      }, 1500);
    };
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    const dc = pc.createDataChannel("oai-events");
    this.dc = dc;
    dc.onopen = () => this._open();
    dc.onmessage = (e) => {
      try { this._event(JSON.parse(e.data)); } catch { /* a malformed frame is not fatal */ }
    };

    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);

    const qs = new URLSearchParams();
    if (this.o.language && this.o.language !== "auto") qs.set("language", this.o.language);
    if (this.o.visitor) qs.set("visitor", this.o.visitor);   // he welcomes a returning name back
    const q = qs.toString() ? `?${qs}` : "";
    const r = await fetch(`${API}/sessions/${this.o.sessionId}/realtime/offer${q}`, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: offer.sdp,
    });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      this.stop();
      const err = new Error(data.detail || `realtime refused (${r.status})`);
      err.response = { status: r.status, data };
      throw err;
    }
    const data = await r.json();
    this.model = data.model || "";
    this.greeting = data.greeting || "";
    this.history = data.history || [];
    this.engine = data.voice === "elevenlabs" ? "elevenlabs" : "openai";
    this.voiceName = data.voice_name || "";
    // two voices from the same provider are not the same loudness; the Lab carries the correction
    this.trim = Number(data.voice_trim) || 1;
    setTrim(this.engine === "elevenlabs" ? this.trim : 1);
    this.notes = data.notes !== false;
    await pc.setRemoteDescription({ type: "answer", sdp: data.sdp });
    this.startedAt = Date.now();
    this.lineNo = ++lineCount;
    this._touch();
    this._mediaSession();
    setTimeout(() => this._diag("line-open"), 1200);
    this.o.onGreeting?.(this.greeting);
    this.o.onState?.("idle");
    return data;
  }

  // WHICH CHANNEL HIS VOICE GOES OUT ON — and what is actually possible.
  // Android: nothing in a web page can move it off the voice-call stream. Chrome keeps the device in
  // MODE_IN_COMMUNICATION while a mic is live, so the handset keys move CALL volume, which has a floor
  // and never reaches zero. Laundering the track through a Web Audio MediaStreamDestination was tried
  // and produced SILENCE on a real handset (remote WebRTC audio through Web Audio is a long-standing
  // Chromium dead end on Android) — do not reintroduce it. The element plays the raw track and
  // `el.volume`, which Android honours all the way to zero, is the control that works.
  // iOS: element playback of a live track goes to the EARPIECE and `el.volume` is read-only, so there
  // the Web Audio speaker path is used, with the element as the fallback.
  setOutput(mode) {
    this.speaker = mode !== "private";
    this._route();
  }

  setVolume(v) {
    this.vol = Math.max(0, Math.min(1, v));
    if (this.engine === "elevenlabs") {
      this._applyVol();
      setVolume(this.vol);
      return;
    }
    // el.volume reaches true zero on Android, which the handset's call volume never does
    if (this.el) { this.el.volume = this.vol; this.el.muted = isIOS() && this.speaker ? this.el.muted : this.vol === 0; }
    setVolume(isIOS() && this.speaker ? this.vol * 1.5 : this.vol);
  }

  // THE ELEMENT'S OWN VOLUME IS APPLIED BEFORE WEB AUDIO TAPS IT. So while the graph is carrying the
  // sound, leaving `el.volume` at the slider value attenuates twice — 0.7 on the element and 0.7
  // again on the gain is 0.49, a good 6 dB thrown away, and the top of the slider never reaches full
  // volume. The element stays wide open and the gain alone carries the slider and the voice trim.
  _applyVol() {
    const el = this.el;
    if (!el) return;
    el.volume = speakerLive() ? 1 : (this.vol ?? 1);
    el.muted = this.vol === 0;
  }

  // Getting it actually playing is the fragile part on a phone: a mobile browser will refuse a
  // play() that no longer sits inside a tap, and it does so silently. So we retry, and if it still
  // has not started we ask the listener for one tap rather than leaving them in silence.
  _play(attempt = 0) {
    const el = this.el;
    if (!el || this.closed) return;
    if (!el.src && !el.srcObject) return;   // nothing loaded yet; there is nothing to start
    resume();
    el.muted = isIOS() && this.speaker ? el.muted : this.vol === 0;
    if (this.vol === 0) return;   // they have pulled him to silence on purpose
    el.play()
      .then(() => { this.playErr = ""; this.o.onNeedsTap?.(false); })
      .catch((e) => {
        this.playErr = `${e.name}: ${e.message}`.slice(0, 120);
        if (attempt < 3) return setTimeout(() => this._play(attempt + 1), 350 * (attempt + 1));
        this.o.onNeedsTap?.(true);
        this._diag("play-refused");
      });
    this._watchProgress();
  }

  // "connected but silent" is the failure we cannot see: the only honest test is whether the
  // element's clock is actually moving.
  _watchProgress() {
    clearTimeout(this.playCheck);
    const mark = this.el?.currentTime ?? 0;
    this.playCheck = setTimeout(() => {
      if (this.closed || !this.el) return;
      const moved = (this.el.currentTime || 0) > mark + 0.05;
      const stalled = this.el.paused || this.el.readyState === 0 || !moved;
      if (stalled) {
        this.o.onNeedsTap?.(true);
        this._diag("audio-stalled");
      } else {
        this._diag("audio-playing");
      }
    }, 2600);
  }

  // what this device actually sees, kept for the curators so a fault nobody can reproduce is still
  // diagnosable
  _diag(event) {
    const el = this.el;
    const t = this.remote?.getAudioTracks?.()[0];
    const data = {
      ios: isIOS(),
      speaker: !!this.speaker,
      vol: this.vol,
      pc: this.pc?.connectionState || "none",
      ice: this.pc?.iceConnectionState || "none",
      ctx: ctxState(),
      paused: el ? el.paused : null,
      muted: el ? el.muted : null,
      elVol: el ? el.volume : null,
      ready: el ? el.readyState : null,
      time: el ? Number((el.currentTime || 0).toFixed(2)) : null,
      sinkCount: document.querySelectorAll("audio").length,
      graph: speakerLive(),
      captured: playbackCaptured(el),
      trim: this.trim ?? 1,
      gain: gainValue(),
      track: t ? `${t.readyState}/${t.muted ? "muted" : "live"}/${t.enabled}` : "none",
      line: this.lineNo || 1,
      engine: this.engine,
      marks: this.t,
    };
    fetch(`${API}/sessions/${this.o.sessionId}/client-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, data: this.playErr ? { ...data, playErr: this.playErr } : data }),
      keepalive: true,
    }).catch(() => {});
  }

  // Re-point a live line at a different language without reconnecting: the prompt is rebuilt on the
  // server and handed to the open session. The rebuilt prompt is ~9k tokens, so a redundant update
  // is a redundant bill — same language, or one we already hold, costs nothing.
  async setLanguage(language) {
    if (this.o.language === language && this.sentLang === language) return true;
    this.o.language = language;
    this._touch(true);
    try {
      this.instructionCache = this.instructionCache || {};
      let instructions = this.instructionCache[language];
      if (!instructions) {
        const r = await fetch(`${API}/sessions/${this.o.sessionId}/realtime/instructions?language=${language}`);
        if (!r.ok) return false;
        instructions = (await r.json()).instructions;
        this.instructionCache[language] = instructions;
      }
      this._send({ type: "session.update", session: { instructions } });
      this.sentLang = language;
      return true;
    } catch {
      return false;
    }
  }

  // A LINE LEFT OPEN STILL BILLS. WebRTC streams audio whether or not anybody is talking, so a tab
  // forgotten on a desk quietly spends all afternoon. After three minutes of silence he says one
  // line and hangs up himself.
  _touch(user = false) {
    if (this.closed) return;
    if (this.farewell && !user) return;   // he is saying goodbye; his own voice must not reopen the line
    if (user) this.farewell = false;
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this._idleOut(), IDLE_MS);
  }

  _idleOut() {
    if (this.closed) return;
    this.farewell = true;
    this.o.onIdleWarning?.();
    this._send({
      type: "response.create",
      response: {
        instructions: "They have gone quiet for a long while. Say one short warm line — that you will be here when they come back — and ask nothing. Then stop.",
      },
    });
    this.idleTimer = setTimeout(() => {
      if (this.closed) return;
      this.o.onIdleClose?.();
      this.stop();
    }, IDLE_FAREWELL_MS);
  }

  // one tap from the listener, inside a real gesture, is enough to start everything
  wake() {
    this._diag("wake-tapped");
    resume();
    if (this.el) { this.el.muted = false; this._applyVol(); }
    this._route();
    this._play();
  }

  _route() {
    if (this.engine === "elevenlabs") return;   // his voice is a plain element here, not a live track
    if (!this.remote || !this.el) return;
    this.el.srcObject = this.remote;
    // If a human voice already spoke through this element earlier in the page's life, the element
    // has no sound of its own left — the track has to go out through Web Audio too, or the
    // understudy taking over after ElevenLabs runs dry would arrive silent.
    if ((isIOS() && this.speaker) || playbackCaptured(this.el)) {
      const live = attachRemote(this.remote, true);
      this.el.muted = live;          // only silence the element once our own path carries it
    } else {
      releaseOutput();
      attachRemote(this.remote, false);   // the analyser still watches the track
      this.el.muted = false;
    }
    this.setVolume(this.vol ?? 1);
    this._play();
    resume();
  }

  // so the phone knows something is playing, and can stop it from the lock screen
  _mediaSession() {
    const ms = navigator.mediaSession;
    if (!ms || !window.MediaMetadata) return;
    try {
      ms.metadata = new window.MediaMetadata({
        title: "Griot",
        artist: this.o.labName || "A flowing conversation",
        album: "Voice conversation",
      });
      ms.playbackState = "playing";
      ms.setActionHandler("pause", () => this.o.onLockStop?.());
      ms.setActionHandler("stop", () => this.o.onLockStop?.());
    } catch { /* not supported here */ }
  }

  _send(obj) {
    if (this.dc?.readyState === "open") this.dc.send(json(obj));
  }

  // The model waits to be spoken to, so Griot's welcome has to be asked for.
  _open() {
    if (this.greeted) return;
    this.greeted = true;

    // A conversation already under way is not restarted: what was said — spoken or typed — is handed
    // to the new line, and he picks the thread up instead of greeting anybody.
    if (this.history?.length) {
      for (const m of this.history) {
        this._send({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: m.role === "assistant" ? "assistant" : "user",
            content: [{ type: m.role === "assistant" ? "output_text" : "input_text", text: String(m.content || "").slice(0, 1200) }],
          },
        });
      }
      this._send({ type: "response.create", response: { instructions: "You are mid-conversation and the voice has just come back — the exchanges above already happened between you and this same person. Do NOT greet them, do NOT welcome them, do NOT introduce yourself and do NOT start over. One short line that shows you are still holding the thread, picking up the last thing they said, then stop and listen." } });
      return;
    }

    if (!this.greeting) return this._send({ type: "response.create" });
    const sw = this.o.language !== "en";
    this._send({ type: "response.create", response: { instructions: `Say exactly these words, warmly and unhurried, then stop and listen: "${this.greeting}"\n${sw ? "Any Kiswahili in it is spoken as a Kenyan would speak it — full open vowels, even stress, not an English approximation. When the line turns to English, let it turn naturally, the way someone in the room switches languages mid-breath. Never translate yourself and never explain the switch." : "It is English throughout; add no Kiswahili."} Add nothing.` } });
  }

  async _search(callId, args) {
    let query = "";
    try { query = JSON.parse(args || "{}").query || ""; } catch { /* keep it empty */ }
    let payload = { passages: [], sources: [] };
    try {
      const r = await fetch(`${API}/sessions/${this.o.sessionId}/realtime/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: json({ query }),
      });
      if (r.ok) payload = await r.json();
    } catch { /* an empty archive answer is better than a dropped turn */ }
    this.sources = payload.sources || [];
    this.o.onSources?.(this.sources);
    if (payload.pull?.text) this.o.onNote?.(payload.pull);   // the archive answered: no judgement needed
    this._send({
      type: "conversation.item.create",
      item: { type: "function_call_output", call_id: callId, output: json({ passages: payload.passages }) },
    });
    this._send({ type: "response.create" });
  }

  // Griot putting a few words on the screen, when he chooses to mid-answer.
  _note(callId, args) {
    this.noteCount = (this.noteCount || 0) + 1;
    this.noted = true;
    try {
      this._show(JSON.parse(args || "{}"));
    } catch { /* ignore a malformed note */ }
    this._send({
      type: "conversation.item.create",
      item: { type: "function_call_output", call_id: callId, output: json({ shown: true }) },
    });
    this._send({ type: "response.create" });
  }

  _show(n) {
    const lines = Array.isArray(n.lines) ? n.lines.map((l) => String(l || "").trim()).filter(Boolean).slice(0, 5) : [];
    const text = String(n.text || "").trim();
    const kind = ["idea", "step", "quote", "name"].includes(n.kind) ? n.kind : "idea";
    if (lines.length > 1) {
      // a set: the points appear in the order they are spoken, as one group
      const group = `g${Date.now()}`;
      lines.forEach((line, i) => {
        setTimeout(() => this.o.onNote?.({ text: line.slice(0, 90), kind: "step", group, index: i + 1, of: lines.length }), i * 550);
      });
      return;
    }
    const one = text || lines[0];
    if (one) this.o.onNote?.({ text: one.slice(0, 90), kind: kind === "step" ? "idea" : kind });
  }

  // What he should call them. Their own device is where it lives; the server holds it for the sitting
  // and writes it down only if they chose to be archived.
  _name(callId, args) {
    let name = "";
    let refused = false;
    try {
      const a = JSON.parse(args || "{}");
      name = String(a.name || "").trim().replace(/[^\p{L}\p{M}\s'’-]/gu, "").slice(0, 40);
      refused = Boolean(a.refused);
    } catch { /* ignore a malformed name */ }
    if (refused) name = "";
    this.o.onName?.({ name, refused });
    fetch(`${API}/sessions/${this.o.sessionId}/visitor`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, refused }),
    }).catch(() => {});
    this._send({
      type: "conversation.item.create",
      item: { type: "function_call_output", call_id: callId, output: json({ remembered: name || false }) },
    });
    this._send({ type: "response.create" });
  }

  /* ── The real voice ──────────────────────────────────────────────────────────────────────────
     In this mode the model never speaks: it writes, sentence by sentence, and a human voice reads
     each sentence aloud. One sentence is synthesised while the previous one is still playing, so
     the seam between them is inaudible. Everything carries a generation number, so the moment the
     listener cuts in, every sentence still in flight is abandoned instead of arriving late. */

  _write(delta = "", flush = false) {
    if (this.engine !== "elevenlabs") return;
    if (delta) {
      if (!this.t.text) this.t.text = Date.now() - this.t.ask;
      this.buf += delta;
      this.said += delta;
      this.o.onGriotPartial?.(this.said);
    }
    const cut = () => (this.head ? headCut(this.buf) : cutPoint(this.buf));
    for (let at = cut(); at > 0; at = cut()) {
      const line = this.buf.slice(0, at).trim();
      this.buf = this.buf.slice(at);
      if (line) {
        this.head = false;
        this._enqueue(line);
      }
    }
    if (flush) {
      const rest = this.buf.trim();
      this.buf = "";
      if (rest) { this.head = false; this._enqueue(rest); }
    }
  }

  _enqueue(text) {
    this.queue.push({ text, gen: this.gen, p: null });
    this._prefetch();
    this._pump();
  }

  // two sentences ahead is enough to cover the gap and little enough to waste almost nothing when
  // the listener interrupts
  _prefetch() {
    let ahead = 0;
    for (const item of this.queue) {
      if (ahead >= 2) break;
      if (!item.p && item.gen === this.gen) item.p = this._synth(item.text);
      ahead += 1;
    }
  }

  async _synth(text) {
    try {
      const r = await fetch(`${API}/sessions/${this.o.sessionId}/say`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: json({ text }),
      });
      if (r.status === 503 || r.status === 409) { this._voiceGone(); return null; }
      if (!r.ok) return null;
      const d = await r.json();
      if (!this.t.synth) this.t.synth = Date.now() - this.t.ask;
      return d.token ? `${API}/speech/${d.token}` : null;
    } catch {
      return null;
    }
  }

  async _pump() {
    if (this.pumping || this.closed) return;
    this.pumping = true;
    try {
      while (this.queue.length && !this.closed) {
        const item = this.queue.shift();
        this._prefetch();
        if (item.gen !== this.gen) continue;
        const url = await (item.p || this._synth(item.text));
        if (!url || this.closed || item.gen !== this.gen) continue;
        await this._sayUrl(url, item.gen);
      }
    } finally {
      this.pumping = false;
      if (!this.queue.length && this.speakingNow && !this.closed) {
        this.speakingNow = false;
        setSource("mic");
        this.o.onState?.("idle");
        this._touch();
      }
    }
  }

  _sayUrl(url, gen) {
    return new Promise((resolve) => {
      const el = this.el;
      if (!el || this.closed || gen !== this.gen) return resolve();
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener("ended", done);
        el.removeEventListener("error", done);
        clearTimeout(timer);
        if (this.stopPlay === done) this.stopPlay = null;
        resolve();
      };
      const timer = setTimeout(done, 70000);
      el.addEventListener("ended", done);
      el.addEventListener("error", done);
      this.stopPlay = done;

      el.srcObject = null;
      el.src = url;
      if (!this.speakingNow) {
        this.speakingNow = true;
        if (!this.t.audio) this.t.audio = Date.now() - this.t.ask;
        this.o.onState?.("speaking");
      }
      resume();
      const audible = attachPlayback(el);
      if (!audible && playbackCaptured(el)) this._diag("graph-silent");   // must never happen again
      this._applyVol();            // after the attach: whether the graph carries it decides this
      setSource("playback");
      el.play()
        .then(() => { this.playErr = ""; this.o.onNeedsTap?.(false); })
        .catch((e) => {
          this.playErr = `${e.name}: ${e.message}`.slice(0, 120);
          this.o.onNeedsTap?.(true);
          this._diag("play-refused");
          done();
        });
    });
  }

  // The listener has cut in, or he has been stopped: drop every sentence still in flight.
  _hush() {
    this.gen += 1;
    this.queue.length = 0;
    this.buf = "";
    this.head = true;
    this.speakingNow = false;
    try { this.stopPlay?.(); } catch { /* already resolved */ }
    const el = this.el;
    if (el && el.src) {
      try { el.pause(); } catch { /* already stopped */ }
      el.removeAttribute("src");
      try { el.load(); } catch { /* nothing buffered */ }
    }
  }

  // The real voice has run out of characters or refused us. Rather than go silent, the line is
  // reopened — the server will have handed the microphone to the understudy by then.
  _voiceGone() {
    if (this.fellBack) return;
    this.fellBack = true;
    this._hush();
    this.o.onVoiceFallback?.();
  }

  _event(e) {
    const t = e.type || "";
    if (t === "input_audio_buffer.speech_started") {
      if (this.engine === "elevenlabs") this._hush();   // he stops the instant they start
      this.o.onState?.("listening");
      setSource("mic");
      return;
    }
    if (t === "input_audio_buffer.speech_stopped") {
      this.o.onState?.("reflecting");
      return;
    }
    if (t.endsWith("input_audio_transcription.completed")) {
      this.heard = (e.transcript || "").trim();
      // real words keep the line open; a room's noise, which transcribes to nothing, does not
      if (this.heard) this._touch(true);
      if (this.heard) this.o.onUser?.(this.heard);
      return;
    }
    if (t === "output_audio_buffer.started" || t.endsWith("audio.delta")) {
      setSource("playback");
      this.o.onState?.("speaking");
      return;
    }
    if (t === "output_audio_buffer.stopped" || t === "output_audio_buffer.cleared") {
      setSource("mic");
      this.o.onState?.("idle");
      return;
    }
    if (t === "response.created") {
      this.responding = true;
      if (this.engine === "elevenlabs") {
        this.head = true;
        this.t = { ask: Date.now() };
      }
      this.o.onState?.("reflecting");
      return;
    }
    if (t === "response.output_text.delta" || t === "response.text.delta") {
      this._write(e.delta || "");
      return;
    }
    if (t === "response.output_text.done" || t === "response.text.done") {
      this._write("", true);
      return;
    }
    if (t.endsWith("audio_transcript.delta")) {
      this.said += e.delta || "";
      this.o.onGriotPartial?.(this.said);
      return;
    }
    if (t.endsWith("audio_transcript.done")) {
      this.said = (e.transcript || this.said || "").trim();
      return;
    }
    if (t === "response.output_item.added" && e.item?.type === "function_call") {
      this.calls[e.item.call_id] = e.item.name;   // the args event doesn't always carry the name
      return;
    }
    if (t === "response.function_call_arguments.done") {
      const name = e.name || this.calls[e.call_id];
      delete this.calls[e.call_id];
      if (name === "show_note") this._note(e.call_id, e.arguments);
      else if (name === "remember_name") this._name(e.call_id, e.arguments);
      else this._search(e.call_id, e.arguments);
      return;
    }
    if (t === "response.done") {
      this.responding = false;
      this._touch();
      const calling = (e.response?.output || []).some((o) => o.type === "function_call");
      if (calling) return; // the archive answer is still on its way
      const said = this.said.trim();
      this.said = "";
      if (said) { this.lastSaid = said; this.o.onGriot?.({ text: said, sources: this.sources }); }
      if (said || this.heard) this._log(this.heard, said);
      this.heard = "";
      if (said && !this.noted) this._writeNote(said);
      this.noted = false;
      return;
    }
    if (t === "error") {
      this.o.onError?.(new Error(e.error?.message || "the line broke"));
    }
  }

  _log(user, griot) {
    fetch(`${API}/sessions/${this.o.sessionId}/realtime/log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: json({ user_text: user, response_text: griot, sources: this.sources }),
    }).catch(() => {});
  }

  setMuted(m) {
    if (!m) this._touch(true);
    this.stream?.getAudioTracks().forEach((t) => (t.enabled = !m));
    if (!m) resume();
  }

  /* The listener tapping the quiet form: "show me that." */
  pullNote() {
    this._touch(true);
    this._writeNote(this.lastSaid || this.said);
  }

  // He is told to leave a note after most turns and mostly does not: that means appending a tool
  // call to a reply he has already finished. Asking him outright afterwards does not work either —
  // a forced tool_choice on response.create is ignored and he answers with words, which a human
  // voice then reads out loud. So the note is written away from the conversation, by a small model
  // reading what he just said.
  async _writeNote(said) {
    const text = (said || "").trim();
    if (!this.notes || this.closed || !text) return;
    try {
      const r = await fetch(`${API}/sessions/${this.o.sessionId}/note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: json({ text }),
      });
      if (!r.ok) return;
      const { note } = await r.json();
      if (note && !this.closed) this._show(note);
    } catch { /* a missing note is never worth interrupting him for */ }
  }

  interrupt() {
    // cancelling when nothing is in flight is an error on the line, and it is noise in the logs
    if (this.responding) this._send({ type: "response.cancel" });
    this.responding = false;
    if (this.engine === "elevenlabs") {
      this._hush();                                  // there is no audio buffer to clear: we hold the voice
    } else {
      this._send({ type: "output_audio_buffer.clear" });
    }
    this.said = "";
    setSource("mic");
    this.o.onState?.("idle");
  }

  stop() {
    if (this.closed) return;
    this.closed = true;
    if (this.engine === "elevenlabs") this._hush();
    const seconds = this.startedAt ? (Date.now() - this.startedAt) / 1000 : 0;
    if (seconds > 1) {
      fetch(`${API}/sessions/${this.o.sessionId}/realtime/usage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: json({ seconds, model: this.model, text: this.engine === "elevenlabs" }),
        keepalive: true,
      }).catch(() => {});
    }
    try { this.dc?.close(); } catch { /* already gone */ }
    try { this.pc?.getSenders().forEach((s) => s.track?.stop()); } catch { /* already gone */ }
    try { this.pc?.close(); } catch { /* already gone */ }
    this.stream?.getAudioTracks().forEach((t) => { t.enabled = false; });   // held, but capturing nothing
    detachStream();
    releaseOutput();
    setSource("mic");
    if (navigator.mediaSession) {
      try {
        navigator.mediaSession.playbackState = "none";
        navigator.mediaSession.metadata = null;
      } catch { /* not supported here */ }
    }
    clearTimeout(this.playCheck);
    clearTimeout(this.idleTimer);
    this.o.onState?.("idle");
    if (this.el) { this.el.pause(); this.el.srcObject = null; this.el = null; }
  }
}

let lineCount = 0;
const IDLE_MS = 3 * 60 * 1000;      // silence this long and the line closes itself
const IDLE_FAREWELL_MS = 9000;      // long enough for him to say the one line first

// The mic is acquired ONCE per page: releasing and re-acquiring it flips Android in and out of
// communication mode, and on some handsets the next line comes up silent. Released on session end.
let mic = null;
export const micStream = async () => {
  if (mic && mic.getAudioTracks().some((t) => t.readyState === "live")) {
    mic.getAudioTracks().forEach((t) => { t.enabled = true; });
    return mic;
  }
  mic = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  return mic;
};

export const releaseMic = () => {
  mic?.getTracks().forEach((t) => t.stop());
  mic = null;
};

// One <audio> for the life of the page. Mobile browsers grant playback permission to an element
// that has already played; a brand new element created after the tap has to earn it again, which is
// how a second flowing line can come up silent.
let sharedEl = null;
export const voiceElement = () => {
  if (sharedEl && document.body.contains(sharedEl)) return sharedEl;
  sharedEl = document.createElement("audio");
  sharedEl.autoplay = true;
  sharedEl.setAttribute("playsinline", "");
  sharedEl.setAttribute("data-testid", "griot-voice");
  document.body.appendChild(sharedEl);
  return sharedEl;
};

// iPhone/iPad, including iPadOS pretending to be a Mac
export const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent)));

export const realtimeSupported = () =>
  typeof window !== "undefined" && !!window.RTCPeerConnection && !!navigator.mediaDevices?.getUserMedia;
